/* Progressive editorial entrances. Native content remains visible without this enhancement. */
(() => {
  'use strict';
  if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;
  const preference = matchMedia('(prefers-reduced-motion: reduce)');
  // Touch browsers do not consistently expose :active during pointer-down.
  // Use a visual state only; native clicks, scrolling and pointer capture stay intact.
  const actions = '.cu-btn, .cu-header__bag, .cu-footer__join, .cu-reviews__more, .cu-reviews__empty button, .cu-locator__submit, .cu-locator__locate, .cu-locator__more';
  let press = null;
  function clearPress() {
    press?.node.removeAttribute('data-cu-button-pressed');
    press = null;
  }
  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'mouse' || !event.isPrimary || preference.matches) return;
    clearPress();
    const node = event.target.closest?.(actions);
    if (!node || node.matches(':disabled, [aria-disabled="true"], [aria-busy="true"]')) return;
    press = { node, id: event.pointerId, x: event.clientX, y: event.clientY };
    node.setAttribute('data-cu-button-pressed', '');
  }, { passive: true });
  document.addEventListener('pointermove', event => {
    if (press && event.pointerId === press.id && Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) clearPress();
  }, { passive: true });
  for (const name of ['pointerup', 'pointercancel']) document.addEventListener(name, event => {
    if (press && event.pointerId === press.id) clearPress();
  }, { passive: true });
  window.addEventListener('blur', clearPress);
  window.addEventListener('pagehide', clearPress);
  preference.addEventListener('change', clearPress);
  const seen = new WeakSet();
  const pending = new Set();
  const running = new Map();
  const targets = [
    '.cu-ha-heading', '.cu-ha-science__photo', '.cu-ha-science__copy',
    '.cu-ha-pores__copy', '.cu-ha-bundles__image', '.cu-ha-bundles__copy', '.cu-ha-retail__copy',
    '.cu-astra__results-copy', '.cu-astra__results-photo',
    '.cu-astra__science-copy', '.cu-astra__science-photo', '.cu-astra__steps > li',
    '.cu-astra__ingredient-layout > :first-child', '.cu-astra__pore-heading',
    '.cu-astra__extractor-layout > div', '.cu-astra__retail > div',
    '.cu-astra .cu-cmp__title', '.cu-astra .cu-faq__title',
    '.cu-kit-heading', '.cu-kit-closing',
  ].join(',');
  function enter(node) {
    try {
      const animation = node.animate([
        { opacity: 0, transform: 'translateY(18px)' },
        { opacity: 1, transform: 'none' },
      ], { duration: 640, easing: 'cubic-bezier(.22, .7, .3, 1)' });
      running.set(node, animation);
      animation.finished.then(() => running.delete(node), () => running.delete(node));
    } finally {
      node.removeAttribute('data-cu-motion-pending');
    }
  }
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const node = entry.target;
      if (!pending.has(node)) continue;
      observer.unobserve(node); pending.delete(node); seen.add(node);
      if (preference.matches || document.hidden || node.contains(document.activeElement)) {
        node.removeAttribute('data-cu-motion-pending'); continue;
      }
      enter(node);
    }
  }, { threshold: .08, rootMargin: `0px 0px -${Math.min(140, Math.round(innerHeight * .16))}px 0px` });

  function mount(root = document) {
    if (preference.matches) return;
    for (const node of root.querySelectorAll(targets)) {
      if (seen.has(node) || pending.has(node)) continue;
      const box = node.getBoundingClientRect();
      // Initial viewport, large reading blocks, anchors and hidden content render immediately.
      if (!box.height || box.top < innerHeight || box.height > innerHeight * .85 || node.contains(document.activeElement)) {
        seen.add(node); continue;
      }
      observer.observe(node); pending.add(node); node.setAttribute('data-cu-motion-pending', '');
    }
  }

  function finishFor(target) {
    if (!(target instanceof Element)) return;
    for (const node of pending) {
      if (target === node || node.contains(target) || target.contains(node)) {
        node.removeAttribute('data-cu-motion-pending');
        observer.unobserve(node); pending.delete(node); seen.add(node);
      }
    }
    for (const [node, animation] of running) {
      if (target === node || node.contains(target) || target.contains(node)) animation.finish();
    }
  }
  function finishAll() {
    observer.disconnect();
    for (const node of pending) { node.removeAttribute('data-cu-motion-pending'); seen.add(node); }
    pending.clear();
    for (const animation of running.values()) animation.finish();
  }
  function revealAnchor() {
    try { if (location.hash) finishFor(document.getElementById(decodeURIComponent(location.hash.slice(1)))); } catch { /* Malformed fragments keep native browser behavior. */ }
  }
  document.addEventListener('focusin', event => finishFor(event.target));
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin === location.origin && url.pathname === location.pathname && url.hash) {
      try { finishFor(document.getElementById(decodeURIComponent(url.hash.slice(1)))); } catch { /* Native fragment handling. */ }
    }
  });
  window.addEventListener('hashchange', revealAnchor);
  window.addEventListener('pagehide', finishAll);
  window.addEventListener('beforeprint', finishAll);
  document.addEventListener('visibilitychange', () => { if (document.hidden) for (const animation of running.values()) animation.finish(); });
  preference.addEventListener('change', () => { if (preference.matches) finishAll(); else mount(); });
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    finishFor(event.target);
  });
  mount(); revealAnchor();
  const hero = document.querySelector('.cu-ha-hero__copy');
  if (hero && !preference.matches && !document.hidden && !location.hash && scrollY < 80) {
    requestAnimationFrame(() => {
      if (!preference.matches && !document.hidden && scrollY < 80 && !hero.contains(document.activeElement)) enter(hero);
    });
  }
})();
